'use client';

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type CSSProperties,
  type FocusEvent,
  type KeyboardEvent,
  type PointerEvent,
  type RefObject,
} from 'react';
import { nearestIndex } from './inspection';

export type InspectionSource = 'pointer' | 'touch' | 'keyboard';

interface Inspection {
  active: number | null;
  source: InspectionSource | null;
}

const REST: Inspection = { active: null, source: null };
const STYLE: CSSProperties = { touchAction: 'pan-y' };

/**
 * Which point of a chart is being inspected, driven by mouse, touch and keyboard.
 * `xs` are the points' x positions in viewBox units and `viewWidth` the viewBox
 * width, so the pointer maps onto the drawing at any rendered size. A touch pins
 * its point until a tap lands outside the chart, unless the touch turns into a page
 * scroll; `start` is where the arrows set
 * off from (the last point when omitted). `onActivate` opens the inspected point on a
 * mouse click or Enter; a tap only inspects, so it never opens anything by itself.
 */
export function useChartInspection<T extends Element = Element>(
  xs: number[],
  viewWidth: number,
  start?: number,
  onActivate?: (index: number) => void,
) {
  const ref = useRef<T>(null);
  const lastPointer = useRef<string>('');
  const [state, setState] = useState<Inspection>(REST);
  const reset = useCallback(() => setState(REST), []);
  const last = xs.length - 1;

  useEffect(() => {
    if (state.source !== 'touch') return;
    // The pinned tooltip's own link sits outside the chart and must survive its tap.
    const onDown = (e: Event) => {
      const target = e.target as Element;
      if (!ref.current?.contains(target) && !target.closest?.('[data-chart-action]')) {
        setState(REST);
      }
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [state.source]);

  const fromPointer = (e: PointerEvent<T>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    if (rect.width <= 0 || xs.length === 0) return;
    const x = ((e.clientX - rect.left) * viewWidth) / rect.width;
    lastPointer.current = e.pointerType;
    const source = e.pointerType === 'touch' ? 'touch' : 'pointer';
    setState({ active: nearestIndex(xs, x), source });
  };

  const onKeyDown = (e: KeyboardEvent<T>) => {
    if (e.key === 'Escape') return reset();
    if (e.key === 'Enter') {
      if (state.active !== null) onActivate?.(state.active);
      return;
    }
    if (last < 0) return;
    const from = state.active ?? Math.min(start ?? last, last);
    const next: Record<string, number> = {
      ArrowLeft: Math.max(0, from - 1),
      ArrowRight: Math.min(last, from + 1),
      Home: 0,
      End: last,
    };
    if (!(e.key in next)) return;
    e.preventDefault();
    setState({ active: next[e.key], source: 'keyboard' });
  };

  return {
    active: state.active,
    source: state.source,
    reset,
    bind: {
      ref: ref as RefObject<T>,
      tabIndex: 0,
      style: STYLE,
      onPointerDown: fromPointer,
      onPointerMove: fromPointer,
      onPointerLeave: (e: PointerEvent<T>) => {
        if (e.pointerType !== 'touch') reset();
      },
      // With pan-y a vertical drag becomes a page scroll, and the browser cancels the pointer.
      onPointerCancel: reset,
      onClick: () => {
        if (lastPointer.current !== 'touch' && state.active !== null) onActivate?.(state.active);
      },
      onKeyDown,
      // Tapping the pinned tooltip's link moves focus to it; the point must outlive that.
      onBlur: (e: FocusEvent<T>) => {
        if (!(e.relatedTarget as Element | null)?.closest?.('[data-chart-action]')) reset();
      },
    },
  };
}
